import React from 'react';
import { cn } from '../../../utils/cn';

interface PillTabsProps {
  value: string;
  onChange: (value: string) => void;
  tabs: {
    id: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
  }[];
  className?: string;
  containerClassName?: string;
}

export const PillTabs: React.FC<PillTabsProps> = ({
  value,
  onChange,
  tabs,
  className,
  containerClassName,
}) => {
  return (
    <div
      className={cn(
        'w-full sm:w-auto overflow-x-auto pb-1 flex justify-start sm:justify-center',
        containerClassName
      )}
    >
      <div
        className={cn(
          'inline-flex p-[5px] bg-gray-100 rounded-full shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)] border border-gray-300',
          className
        )}
      >
        {tabs
          .filter((tab) => !tab.disabled)
          .map((tab, index) => {
            const isActive = value === tab.id;
            const isFirst = index === 0;
            const isLast = index === tabs.filter((t) => !t.disabled).length - 1;

            return (
              <button
                key={tab.id}
                role="tab"
                onClick={() => onChange(tab.id)}
                className={`
                  relative flex items-center gap-2 px-3 py-1.5 md:py-2 md:px-5 transition-all duration-300 text-xs 2xl:text-[14px]
                  ${isFirst ? 'rounded-l-full' : ''}
                  ${isLast ? 'rounded-r-full' : ''}
                  ${isActive ? 'text-zinc font-bold z-10' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}
                `}
              >
                {/* Active tab background pill with animation */}
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-gray-50 rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.12)] z-0 animate-[pillAppear_0.3s_ease-out] border border-gray-200 overflow-hidden">
                    {/* White highlight at the top of the active pill */}
                    <div className="absolute top-0 left-0 right-0 h-[30%] bg-gradient-to-b from-white to-transparent opacity-80"></div>
                  </div>
                )}

                {/* Icon and label with z-index to appear above the background */}
                <div className="relative z-10 flex items-center gap-2">
                  {tab.icon && (
                    <div
                      className={
                        isActive
                          ? 'text-zinc fill-current'
                          : 'text-gray-500 fill-current group-hover:text-gray-700 transition-colors duration-200'
                      }
                    >
                      {tab.icon}
                    </div>
                  )}
                  <span
                    className={isActive ? 'font-bold' : 'font-medium transition-all duration-200'}
                  >
                    {tab.label}
                  </span>
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
};
